/**
 * Arbre de navigation réordonnable par drag-drop (long-press 500ms), 2 niveaux :
 * - containers racine (Le midi, Nos vins…) réordonnables entre eux
 * - sous-catégories réordonnables au sein de leur container (intra-parent only)
 *
 * Même UX que SortableProductList (items). @dnd-kit, 1 DndContext par liste de
 * frères (scope = intra-parent garanti). Remplace les boutons ↑↓ (2 PUT/refetch
 * par pas) par un drag fluide + 1 PATCH /pos/navigation/reorder.
 */
import React, { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';

export interface NavNode {
  id: string;
  parentId: string | null;
  label: string;
  emoji: string | null;
  hidden: boolean;
  position: number;
  sourceMappings: { provider: string; ids: string[] }[];
}
export interface PosCategoryRef {
  provider: string;
  categoryId: string;
  productCount: number;
}

interface Props {
  nodes: NavNode[];
  posCategories: PosCategoryRef[];
  onReorder: (parentId: string | null, orderedIds: string[]) => void;
  onEdit: (node: NavNode) => void;
  onDelete: (id: string) => void;
  onAddChild: (parentId: string | null) => void;
}

function productCount(node: NavNode, posCategories: PosCategoryRef[]): number {
  return node.sourceMappings.reduce((sum, m) => {
    for (const id of m.ids) {
      const cat = posCategories.find((c) => c.provider === m.provider && c.categoryId === id);
      if (cat) sum += cat.productCount;
    }
    return sum;
  }, 0);
}

const stop = (e: React.PointerEvent) => e.stopPropagation();

function NodeCard({
  node, posCategories, isContainer, onEdit, onDelete, onAddChild, childrenSlot,
}: {
  node: NavNode; posCategories: PosCategoryRef[]; isContainer: boolean;
  onEdit: (n: NavNode) => void; onDelete: (id: string) => void; onAddChild: (pid: string | null) => void;
  childrenSlot?: React.ReactNode;
}) {
  const { setNodeRef, transform, transition, listeners, attributes, isDragging } = useSortable({ id: node.id });
  const count = productCount(node, posCategories);
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    zIndex: isDragging ? 30 : undefined,
    position: isDragging ? 'relative' : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} className={`navc-wrap ${isContainer ? 'container' : 'child'} ${isDragging ? 'dragging' : ''}`}>
      <div className={`navc-card ${node.hidden ? 'is-hidden' : ''}`} {...attributes} {...listeners}>
        <span className="sortable-grip" aria-hidden="true">⠿</span>
        <span className="navc-emoji">{node.emoji ?? (isContainer ? '📂' : '📁')}</span>
        <span className="navc-label">{node.label}</span>
        {count > 0 && <span className="navc-count">{count} prod</span>}
        {node.hidden && <span className="navc-hidden">masqué</span>}
        <div className="navc-actions">
          <button type="button" className="navc-btn" onPointerDown={stop} onClick={() => onEdit(node)} aria-label="Éditer">✏️</button>
          <button type="button" className="navc-btn danger" onPointerDown={stop} onClick={() => onDelete(node.id)} aria-label="Supprimer">🗑️</button>
          {isContainer && (
            <button type="button" className="navc-btn" onPointerDown={stop} onClick={() => onAddChild(node.id)} aria-label="Ajouter une sous-catégorie">＋</button>
          )}
        </div>
      </div>
      {childrenSlot}
    </div>
  );
}

export function SortableNavTree({ nodes, posCategories, onReorder, onEdit, onDelete, onAddChild }: Props) {
  const [local, setLocal] = useState<NavNode[]>(nodes);
  useEffect(() => { setLocal(nodes); }, [nodes]);

  const sensors = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 500, tolerance: 5 } }),
    useSensor(MouseSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const byPos = (a: NavNode, b: NavNode) => a.position - b.position;
  const containers = local.filter((n) => n.parentId == null).sort(byPos);
  const childrenOf = (id: string) => local.filter((n) => n.parentId === id).sort(byPos);

  const reorderGroup = (parentId: string | null, activeId: string, overId: string) => {
    const group = local.filter((n) => n.parentId === parentId).sort(byPos);
    const oldI = group.findIndex((n) => n.id === activeId);
    const newI = group.findIndex((n) => n.id === overId);
    if (oldI < 0 || newI < 0) return; // over un autre groupe → no-op (intra-parent only)
    const moved = arrayMove(group, oldI, newI);
    const posById = new Map(moved.map((n, i) => [n.id, i]));
    setLocal((prev) => prev.map((n) => (posById.has(n.id) ? { ...n, position: posById.get(n.id)! } : n)));
    onReorder(parentId, moved.map((n) => n.id));
  };

  const onDragStart = () => { try { navigator.vibrate?.(50); } catch { /* no-op */ } };
  const makeDragEnd = (parentId: string | null) => (e: DragEndEvent) => {
    if (e.over && e.active.id !== e.over.id) reorderGroup(parentId, String(e.active.id), String(e.over.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={onDragStart}
      onDragEnd={makeDragEnd(null)}
    >
      <SortableContext items={containers.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        {containers.map((container) => {
          const kids = childrenOf(container.id);
          return (
            <NodeCard
              key={container.id}
              node={container}
              posCategories={posCategories}
              isContainer
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              childrenSlot={
                <div className="navc-children">
                  {kids.length > 0 && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                      onDragStart={onDragStart}
                      onDragEnd={makeDragEnd(container.id)}
                    >
                      <SortableContext items={kids.map((k) => k.id)} strategy={verticalListSortingStrategy}>
                        {kids.map((kid) => (
                          <NodeCard
                            key={kid.id}
                            node={kid}
                            posCategories={posCategories}
                            isContainer={false}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onAddChild={onAddChild}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                  <button type="button" className="navc-add-child" onClick={() => onAddChild(container.id)}>
                    + sous-catégorie
                  </button>
                </div>
              }
            />
          );
        })}
      </SortableContext>
    </DndContext>
  );
}
